import { createTheme, ThemeProvider, styled, alpha } from "@mui/material/styles";

// Helper function to create tinted shadows
const createTintedShadow = (color, elevation) => {
  const opacity = Math.min(0.1 + elevation * 0.01, 0.2); // 10-20% opacity
  const blur = 4 + elevation * 2; // 4-24px blur
  const yOffset = 1 + elevation * 0.5; // 1-8px Y-offset
  
  return `0px ${yOffset}px ${blur}px ${alpha(color, opacity)}`;
};

// Primary color for shadow tinting
const primaryColor = "#00668B";

// Custom shadow system with tinted shadows
const customShadows = [
  'none',
  createTintedShadow(primaryColor, 1), // Card shadow
  createTintedShadow(primaryColor, 2),
  createTintedShadow(primaryColor, 3),
  createTintedShadow(primaryColor, 4), // Hover shadow
  createTintedShadow(primaryColor, 5),
  createTintedShadow(primaryColor, 6),
  createTintedShadow(primaryColor, 7),
  createTintedShadow(primaryColor, 8), // Modal shadow
  createTintedShadow(primaryColor, 9),
  createTintedShadow(primaryColor, 10),
  createTintedShadow(primaryColor, 11),
  createTintedShadow(primaryColor, 12),
  createTintedShadow(primaryColor, 13),
  createTintedShadow(primaryColor, 14),
  createTintedShadow(primaryColor, 15),
  createTintedShadow(primaryColor, 16),
  createTintedShadow(primaryColor, 17),
  createTintedShadow(primaryColor, 18),
  createTintedShadow(primaryColor, 19),
  createTintedShadow(primaryColor, 20),
  createTintedShadow(primaryColor, 21),
  createTintedShadow(primaryColor, 22),
  createTintedShadow(primaryColor, 23),
  createTintedShadow(primaryColor, 24),
];

const theme = createTheme({
  // Explicit 8px spacing base unit (MUI default, but making it explicit)
  spacing: 8,
  
  typography: {
    fontFamily: "Inter, sans-serif",
    // Typography scale with clear hierarchy
    h1: {
      fontSize: '3rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '2.25rem',
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontSize: '1.875rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.4,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.4,
      letterSpacing: 0,
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 500,
      lineHeight: 1.4,
      letterSpacing: 0,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
      fontWeight: 400,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      fontWeight: 400,
    },
    button: {
      fontWeight: 500,
      textTransform: 'none',
    },
  },
  
  palette: {
    primary: {
      main: "#00668B",
      light: "#3389A8",
      dark: "#004d6b",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#c0c0c0",
      light: "#d4d4d4",
      dark: "#9a9a9a",
      contrastText: "#000000",
    },
    background: {
      default: "#f8f7ff",
      paper: "#ffffff",
    },
    text: {
      primary: "rgba(0, 0, 0, 0.87)",
      secondary: "rgba(0, 0, 0, 0.6)",
      disabled: "rgba(0, 0, 0, 0.38)",
    },
    // Custom accent color
    accent: {
      main: "#FF4770",
      light: "#ff6b8a",
      dark: "#ff2d55",
      contrastText: "#ffffff",
    },
    // Semantic colors
    success: {
      main: "#4caf50",
      light: "#81c784",
      dark: "#388e3c",
      contrastText: "#ffffff",
    },
    error: {
      main: "#f44336",
      light: "#e57373",
      dark: "#d32f2f",
      contrastText: "#ffffff",
    },
    warning: {
      main: "#ff9800",
      light: "#ffb74d",
      dark: "#f57c00",
      contrastText: "#000000",
    },
    info: {
      main: "#2196f3",
      light: "#64b5f6",
      dark: "#1976d2",
      contrastText: "#ffffff",
    },
  },

  // Custom shadow system
  shadows: customShadows,

  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      customBreakpoint: 1600,
    },
  },

  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 30,
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fill: "currentColor",
        },
      },
    },
  },
});

// Add gradient utilities to theme
theme.gradients = {
  primary: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  accent: `linear-gradient(135deg, ${theme.palette.accent.main} 0%, ${theme.palette.accent.dark} 100%)`,
  // Subtle background gradients
  backgroundSubtle: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
};

export default theme;
